/**
 * Attack Chain Visualization Component
 *
 * Displays attack chains in a table overview, with the ability to click into
 * individual chains to see detailed React Flow visualizations showing the
 * progression of an attack from initial access to final impact.
 */

import React, { useMemo, useCallback, useState } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Background,
  Controls,
  Position,
  MarkerType,
  BackgroundVariant,
  Handle,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { AttackChain } from '../api/client';
import { Shield, AlertTriangle, Info, ShieldX, ChevronRight, ChevronLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface AttackChainVisualizationProps {
  attackChains: AttackChain[];
  onStepClick?: (chainId: string, stepNumber: number, findingId?: string) => void;
}

// Severity styling (matching TopologyMap theme with dark mode support)
const getSeverityStyles = (severity: string) => {
  switch (severity.toLowerCase()) {
    case 'critical':
      return {
        card: 'border-red-600 bg-red-50 dark:border-red-400 dark:bg-red-950/20',
        badge: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-950/30 dark:text-red-200 dark:border-red-700',
        text: 'text-red-800 dark:text-red-200',
        icon: <ShieldX className="h-4 w-4 text-red-600 dark:text-red-400" />,
        edgeColor: '#dc2626', // red-600 for light mode
      };
    case 'high':
      return {
        card: 'border-orange-600 bg-orange-50 dark:border-orange-400 dark:bg-orange-950/20',
        badge: 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-950/30 dark:text-orange-200 dark:border-orange-700',
        text: 'text-orange-800 dark:text-orange-200',
        icon: <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />,
        edgeColor: '#ea580c', // orange-600 for light mode
      };
    case 'medium':
      return {
        card: 'border-amber-600 bg-amber-50 dark:border-amber-400 dark:bg-amber-950/20',
        badge: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-700',
        text: 'text-amber-800 dark:text-amber-200',
        icon: <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />,
        edgeColor: '#d97706', // amber-600 for light mode
      };
    default:
      return {
        card: 'border-gray-600 bg-gray-50 dark:border-gray-400 dark:bg-gray-950/20',
        badge: 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-950/30 dark:text-gray-200 dark:border-gray-700',
        text: 'text-gray-800 dark:text-gray-200',
        icon: <Shield className="h-4 w-4 text-gray-600 dark:text-gray-400" />,
        edgeColor: '#6b7280', // gray-600 for light mode
      };
  }
};

// Custom node component for attack chain steps (matching TopologyMap style)
const AttackChainStepNode = ({ data }: any) => {
  const styles = getSeverityStyles(data.severity);

  return (
    <div className="relative">
      <Handle type="target" position={Position.Left} className="!bg-primary" />
      <Handle type="source" position={Position.Right} className="!bg-primary" />
      <Card 
        className={`p-4 border-2 transition-all duration-300 hover:shadow-xl cursor-pointer w-[320px] ${styles.card}`}
        onClick={data.onClick}
      >
        <div className="flex items-start gap-3 mb-3">
          {styles.icon}
          <div className="flex-1 min-w-0">
            <div className={`text-xs font-bold uppercase tracking-wide mb-1.5 ${styles.text}`}>
              Step {data.stepNumber}
            </div>
            <div className={`text-sm font-semibold leading-snug ${styles.text}`}>
              {data.stepDescription}
            </div>
          </div>
        </div>

        <div className="text-xs mt-3 pt-3 border-t border-border">
          <div className="font-semibold mb-1 text-foreground">Attacker Gains:</div>
          <div className="leading-relaxed text-foreground">{data.attackerCapability}</div>
        </div>

        {data.findingId && (
          <div className="text-xs mt-3 pt-3 border-t border-border">
            <span className="font-semibold text-foreground">Finding:</span> <span className="text-muted-foreground">{data.findingId.substring(0, 12)}...</span>
          </div>
        )}
      </Card>
    </div>
  );
};

const nodeTypes = {
  attackChainStep: AttackChainStepNode,
};

// Single chain flow visualization component
const AttackChainFlowView: React.FC<{
  chain: AttackChain;
  onStepClick?: (chainId: string, stepNumber: number, findingId?: string) => void;
}> = ({ chain, onStepClick }) => {
  const { nodes, edges } = useMemo(() => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];

    // Layout parameters for single chain view
    const horizontalSpacing = 420;
    const startX = 50;
    const stepsY = 150; // Centered Y position for steps

    const styles = getSeverityStyles(chain.severity);

    // Sort steps by step number
    const sortedSteps = [...chain.steps].sort((a, b) => a.step_number - b.step_number);

    // Add step nodes - all at the same Y position for proper alignment
    sortedSteps.forEach((step, stepIndex) => {
      const nodeId = `chain-${chain.id}-step-${step.step_number}`;
      const x = startX + stepIndex * horizontalSpacing;

      nodes.push({
        id: nodeId,
        type: 'attackChainStep',
        position: { x, y: stepsY }, // All steps at same Y position
        data: {
          stepNumber: step.step_number,
          stepDescription: step.step_description,
          attackerCapability: step.attacker_capability,
          findingId: step.finding_id,
          severity: chain.severity,
          onClick: () => onStepClick?.(chain.id, step.step_number, step.finding_id),
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        draggable: false,
        zIndex: 1, // Lower z-index than header
      });

      // Add edge from previous step
      if (stepIndex > 0) {
        const prevNodeId = `chain-${chain.id}-step-${sortedSteps[stepIndex - 1].step_number}`;
        edges.push({
          id: `edge-${prevNodeId}-${nodeId}`,
          source: prevNodeId,
          target: nodeId,
          type: 'smoothstep',
          animated: true,
          style: { 
            stroke: styles.edgeColor, 
            strokeWidth: 2.5,
            strokeOpacity: 0.9,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 12,
            height: 12,
            color: styles.edgeColor,
          },
        });
      }
    });

    return { nodes, edges };
  }, [chain, onStepClick]);

  const handleNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    // Node click is handled by the custom node components
  }, []);

  return (
    <div className="w-full h-[600px] border rounded-lg overflow-hidden bg-background">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={handleNodeClick}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
        className="bg-background"
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} className="bg-background" />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};

// Main component with table overview
export const AttackChainVisualization: React.FC<AttackChainVisualizationProps> = ({
  attackChains,
  onStepClick,
}) => {
  const [selectedChain, setSelectedChain] = useState<AttackChain | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const getSeverityIcon = (severity: string) => {
    const styles = getSeverityStyles(severity);
    return styles.icon;
  };

  if (attackChains.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <div className="text-center">
          <Info className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="text-foreground">No attack paths identified for this assessment</p>
        </div>
      </div>
    );
  }

  // Pagination logic
  const totalPages = Math.ceil(attackChains.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedChains = attackChains.slice(startIndex, endIndex);

  return (
    <>
      {/* Pagination summary */}
      <div className="flex items-center justify-between mb-4 text-sm text-muted-foreground">
        <div>
          Showing {startIndex + 1}-{Math.min(endIndex, attackChains.length)} of {attackChains.length} attack paths
        </div>
        {totalPages > 1 && (
          <div className="text-xs">
            Page {currentPage} of {totalPages}
          </div>
        )}
      </div>

      <div className="space-y-3">
        {paginatedChains.map((chain) => {
          const styles = getSeverityStyles(chain.severity);
          const sortedSteps = [...chain.steps].sort((a, b) => a.step_number - b.step_number);
          
          return (
            <Card
              key={chain.id}
              className={`cursor-pointer transition-all duration-200 hover:shadow-lg border-l-4 ${styles.card}`}
              onClick={() => setSelectedChain(chain)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  {/* Left side - Chain info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex-shrink-0">
                        {getSeverityIcon(chain.severity)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg text-foreground mb-1 leading-tight">{chain.chain_name}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">{chain.impact_description}</p>
                      </div>
                    </div>
                    
                    {/* Step count and summary */}
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-3 ml-7">
                      <div className="flex items-center gap-1">
                        <span className="font-semibold text-foreground">{sortedSteps.length}</span>
                        <span>step{sortedSteps.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="font-semibold text-foreground">{chain.risk_score.toFixed(1)}/10.0</span>
                        <span>risk score</span>
                      </div>
                    </div>
                  </div>

                  {/* Right side - Severity badge and arrow */}
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge className={`${styles.badge} text-xs font-semibold px-3 py-1`}>
                      {chain.severity.toUpperCase()}
                    </Badge>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>

          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              // Show first page, last page, current page, and pages around current
              const showPage = page === 1 ||
                               page === totalPages ||
                               (page >= currentPage - 1 && page <= currentPage + 1);

              if (!showPage) {
                // Show ellipsis between gaps
                if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="px-2 text-muted-foreground">...</span>;
                }
                return null;
              }

              return (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                  className="w-8 h-8 p-0"
                >
                  {page}
                </Button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Dialog for detailed chain visualization */}
      <Dialog open={selectedChain !== null} onOpenChange={(open) => !open && setSelectedChain(null)}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <div className="flex items-center justify-between gap-6 pr-8">
              <DialogTitle className="text-xl font-bold flex-1">
                {selectedChain?.chain_name || 'Attack Path Details'}
              </DialogTitle>
              {selectedChain && (
                <div className="flex items-center gap-3 shrink-0">
                  <Badge className={getSeverityStyles(selectedChain.severity).badge + ' text-xs font-semibold px-3 py-1'}>
                    {selectedChain.severity.toUpperCase()}
                  </Badge>
                  <span className="text-sm font-semibold text-muted-foreground">
                    Risk: {selectedChain.risk_score.toFixed(1)}/10.0
                  </span>
                </div>
              )}
            </div>
          </DialogHeader>
          <div className="px-6 pb-6">
            {selectedChain && (
              <AttackChainFlowView chain={selectedChain} onStepClick={onStepClick} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AttackChainVisualization;
